import { useState } from "react";
import styled from "styled-components";
import sendEnquiry from "../../data/sendEnquiry";
import { leadRoute } from "../../data/brand";

/*
 * "Would you like a real valuation?" - offered after the automated one, and
 * the point at which a reader becomes a lead.
 *
 * WHY IT BELONGS HERE. Somebody who has just typed their own address into a
 * valuation tool and answered fifteen questions about the condition of the
 * house is about as close to a vendor as a form gets. The automated figure
 * has already told them it is a guide and that they should take advice; this
 * is the offer to arrange exactly that.
 *
 * CONSENT IS SEPARATE, UNTICKED, AND NAMES WHAT TRAVELS. Under UK GDPR,
 * consent to pass someone's details to a third party has to be a free,
 * specific, informed act of its own - not a condition of using the tool, not
 * bundled into a tick about something else, and not pre-set. So the box is
 * empty by default, the form works without it (the enquiry simply comes to
 * us), and the words next to it say which fields go and who to.
 *
 * NOTHING IS POSTED ANYWHERE NEW. It goes through the existing contact-form
 * Lambda, which since October 2026 accepts a fixed set of fields and sends
 * to one fixed recipient - see CLAUDE.md section 6g on why that matters. A
 * new endpoint that accepted a free-form payload would be re-opening the
 * hole that closed.
 */

const Wrap = styled.div`
    margin-top: 1.25rem;
    border: 1px solid ${(props) => props.theme.darkblue};
    border-radius: 10px;
    background: #f4f8fc;
    padding: 1.1rem 1.2rem;
`;

const Head = styled.h3`
    margin: 0 0 0.3rem;
    font-size: 1.02rem;
    font-weight: 600;
    color: ${(props) => props.theme.darkblue};
`;

const Blurb = styled.p`
    margin: 0 0 0.9rem;
    font-size: 0.9rem;
    line-height: 1.5;
    color: ${(props) => props.theme.black};
`;

const Fields = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0.8rem;
    margin-bottom: 0.9rem;
`;

const Field = styled.label`
    display: block;
    min-width: 0;
    span {
        display: block;
        font-size: 0.78rem;
        font-weight: 600;
        color: ${(props) => props.theme.darkblue};
        margin-bottom: 0.3rem;
    }
    input {
        width: 100%;
        box-sizing: border-box;
        padding: 0.6rem 0.7rem;
        font-size: 0.92rem;
        font-family: inherit;
        border: 1px solid ${(props) => props.theme.lightgrey};
        border-radius: ${(props) => props.theme.radius};
        background: #fff;
        &:focus {
            outline: 2px solid ${(props) => props.theme.lightblue};
            outline-offset: 1px;
            border-color: ${(props) => props.theme.darkblue};
        }
    }
`;

const Consent = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 0.9rem;
    cursor: pointer;
    input {
        margin: 0.15rem 0 0;
        width: 1.15rem;
        height: 1.15rem;
        flex: none;
        accent-color: ${(props) => props.theme.darkblue};
    }
    span {
        font-size: 0.85rem;
        line-height: 1.5;
        color: ${(props) => props.theme.black};
    }
`;

const Go = styled.button`
    padding: 0.8rem 1.6rem;
    font-size: 0.98rem;
    font-weight: 600;
    color: #fff;
    background: ${(props) => props.theme.darkblue};
    border: 0;
    border-radius: ${(props) => props.theme.radius};
    cursor: pointer;
    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;

const Note = styled.p`
    margin: 0.7rem 0 0;
    font-size: 0.8rem;
    line-height: 1.5;
    color: ${(props) => props.theme.grey};
`;

const Err = styled.p`
    margin: 0.7rem 0 0;
    font-size: 0.88rem;
    color: ${(props) => props.theme.red};
`;

const Done = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: ${(props) => props.theme.darkblue};
`;

export default function ValuationLead({ address, postcode, estimate, brandName }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [share, setShare] = useState(false);
    const [fax, setFax] = useState(""); // honeypot; see the Lambda
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const where = address || postcode || "this property";

    /*
     * The same route the email is labelled with, used here to keep the
     * consent wording true.
     *
     * "Pass my details to a local estate agent" is accurate for a house in
     * Leeds and wrong for one in SW17, where the local agent IS us and
     * nothing is passed anywhere. Consent has to be informed to be consent,
     * so the box says which of the two is actually going to happen - and the
     * confirmation afterwards says the same thing rather than claiming a
     * referral that never took place.
     */
    const route = leadRoute(postcode);
    const ourArea = route.key === "agency";

    async function submit() {
        setError(null);
        setSending(true);
        try {
            /*
             * The property and the consent are written into the message
             * rather than added as new fields, because the Lambda validates
             * a fixed set and anything else is dropped. One recipient, four
             * fields, no free-form payload - which is what makes this safe
             * to reuse.
             */
            /*
             * The route goes FIRST, before the address and before the
             * figure. Every one of these arrives in the same inbox looking
             * like every other, and the two kinds are worth about £14,400
             * and about £40 - so the one thing the reader must not have to
             * work out for themselves is which one they are holding. See
             * leadRoute in data/brand.js.
             */
            const lines = [
                "VALUATION ENQUIRY from the Doorstep Data property report.",
                "",
                "ROUTE: " + route.label,
                "Worth: " + route.worth,
                "Next: " + route.action,
                "",
                "Property: " + where,
                "Postcode: " + (postcode || "—"),
                "Automated guide figure: " +
                    (estimate || "none - the tool could not produce one for this property"),
                "",
                share
                    ? "CONSENT GIVEN: happy for their details and this property to be passed to a local estate agent for a valuation."
                    : "No consent to pass to a third party. Contact from " + brandName + " only.",
            ];

            /*
             * A referral lead with no consent cannot be sold, and saying so
             * here saves somebody reading the consent line and deciding to
             * chance it. There is nothing to sell; the enquiry is still real
             * and still worth answering.
             */
            if (route.key === "referral" && !share) {
                lines.push("");
                lines.push(
                    "NOT SALEABLE: outside our area and no consent to pass on. Answer it ourselves or refer them with no details attached."
                );
            }
            await sendEnquiry({
                type: "contact",
                name: name.trim(),
                email: email.trim(),
                phone: phone.trim(),
                message: lines.join("\n"),
                fax,
            });
            setSent(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setSending(false);
        }
    }

    if (sent) {
        return (
            <Wrap>
                <Head>Thank you &mdash; that&rsquo;s with us</Head>
                <Done>
                    We&rsquo;ll be in touch about {where}.{" "}
                    {!share
                        ? "We have not passed your details to anyone else."
                        : ourArea
                        ? "That postcode is in an area we cover ourselves, so your details stay with us — nothing has been passed to anyone else."
                        : "That postcode is outside the areas we cover, so we'll pass your details to a local agent who can value it properly."}
                </Done>
            </Wrap>
        );
    }

    return (
        <Wrap>
            <Head>Want a real valuation of {where}?</Head>
            <Blurb>
                {/*
                 * There is not always a figure above. A certificate with no
                 * floor area produces "No estimate" and no number at all,
                 * and the offer is still worth making - somebody who just
                 * tried to value their own house is a vendor whether or not
                 * the tool could help. But the copy cannot point at a figure
                 * that is not there, which is what it did until now.
                 */}
                {estimate
                    ? "The figure above is a guide from public records."
                    : "We could not produce a guide figure for this property from public records."}{" "}
                A valuation is somebody standing in the property, and it is free. Leave your details
                and we&rsquo;ll arrange one.
            </Blurb>

            <Fields>
                <Field>
                    <span>Your name</span>
                    <input
                        id="lead-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoComplete="name"
                    />
                </Field>
                <Field>
                    <span>Email</span>
                    <input
                        id="lead-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                    />
                </Field>
                <Field>
                    <span>Phone (optional)</span>
                    <input
                        id="lead-phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        autoComplete="tel"
                    />
                </Field>
            </Fields>

            {/* Hidden from people, irresistible to bots. The Lambda drops
                anything that fills it, silently. */}
            <label style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                Fax
                <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={fax}
                    onChange={(e) => setFax(e.target.value)}
                />
            </label>

            <Consent>
                <input
                    id="lead-share"
                    type="checkbox"
                    checked={share}
                    onChange={(e) => setShare(e.target.checked)}
                />
                <span>
                    {ourArea ? (
                        <>
                            Please use my{" "}
                            <strong>
                                name, email, phone number and this property&rsquo;s address
                            </strong>{" "}
                            to arrange a valuation. This postcode is in an area we cover ourselves,
                            so your details <strong>stay with us</strong> and are not passed to
                            another agent. I can withdraw this at any time by emailing us.
                        </>
                    ) : (
                        <>
                            Please pass my{" "}
                            <strong>
                                name, email, phone number and this property&rsquo;s address
                            </strong>{" "}
                            to a local estate agent so they can offer me a valuation. I understand{" "}
                            {brandName} may be paid a referral fee, and that I can withdraw this at
                            any time by emailing us.
                        </>
                    )}
                </span>
            </Consent>

            <Go type="button" onClick={submit} disabled={sending || !name.trim() || !email.trim()}>
                {sending ? "Sending…" : "Request a valuation"}
            </Go>

            {error && <Err>{error}</Err>}

            <Note>
                {/* The tick is genuinely optional, and saying so is part of
                    what makes the consent free rather than a toll. */}
                Leaving the box unticked is fine &mdash; your enquiry comes to {brandName} and
                nobody else. See our <a href="/privacy-policy">privacy policy</a> for how we handle
                your details.
            </Note>
        </Wrap>
    );
}
